import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const IntelligentContactForm = () => {
  const [inquiryType, setInquiryType] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    budget: '',
    timeline: '',
    projectType: '',
    urgency: 'normal'
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adaptiveQuestions, setAdaptiveQuestions] = useState([]);

  const inquiryTypes = [
    {
      id: 'project-work',
      name: 'Project Work',
      icon: 'Briefcase',
      description: 'Custom development projects',
      color: 'brand-cyan',
      priority: 'high'
    },
    {
      id: 'mentoring',
      name: 'Mentoring',
      icon: 'GraduationCap',
      description: 'Learning and skill development',
      color: 'brand-green',
      priority: 'medium'
    },
    {
      id: 'speaking',
      name: 'Speaking Engagements',
      icon: 'Mic',
      description: 'Conferences and events',
      color: 'brand-purple',
      priority: 'medium'
    },
    {
      id: 'partnerships',
      name: 'Partnerships',
      icon: 'Handshake',
      description: 'Business collaborations',
      color: 'warning',
      priority: 'high'
    },
    {
      id: 'consultation',
      name: 'Consultation',
      icon: 'MessageSquare',
      description: 'Technical advice and guidance',
      color: 'brand-cyan',
      priority: 'normal'
    }
  ];

  const adaptiveQuestionSets = {
    'project-work': [
      { id: 'projectType', label: 'Project Type', type: 'select', options: ['Web Application', 'Mobile App', 'E-commerce', 'API Development', 'Other'] },
      { id: 'budget', label: 'Budget Range (ZAR)', type: 'select', options: ['R5 000 - R25 000', 'R25 000 - R50 000', 'R50 000 - R250 000', 'R250 000+'] },
      { id: 'timeline', label: 'Timeline', type: 'select', options: ['1-2 weeks', '1 month', '3-6 months', '6-12 months'] },
      { id: 'urgency', label: 'Urgency Level', type: 'select', options: ['Low', 'Normal', 'High', 'Critical'] }
    ],
    'mentoring': [
      { id: 'experience', label: 'Current Experience Level', type: 'select', options: ['Beginner', 'Intermediate', 'Advanced', 'Expert'] },
      { id: 'focus', label: 'Learning Focus', type: 'select', options: ['Frontend', 'Backend', 'Full-Stack', 'DevOps', 'Mobile'] },
      { id: 'commitment', label: 'Time Commitment', type: 'select', options: ['1-2 hours/week', '3-5 hours/week', '5-10 hours/week', '10+ hours/week'] }
    ],
    'speaking': [
      { id: 'eventType', label: 'Event Type', type: 'select', options: ['Conference', 'Workshop', 'Webinar', 'Podcast', 'Panel'] },
      { id: 'audience', label: 'Audience Size', type: 'select', options: ['< 50', '50-200', '200-500', '500+'] },
      { id: 'topic', label: 'Preferred Topic', type: 'select', options: ['React/Frontend', 'Full-Stack Development', 'Career Growth', 'Tech Trends'] }
    ],
    'partnerships': [
      { id: 'partnershipType', label: 'Partnership Type', type: 'select', options: ['Joint Venture', 'Referral Program', 'Technology Integration', 'Content Collaboration'] },
      { id: 'companySize', label: 'Company Size', type: 'select', options: ['Startup', 'Small Business', 'Medium Enterprise', 'Large Corporation'] }
    ],
    'consultation': [
      { id: 'consultationType', label: 'Consultation Type', type: 'select', options: ['Technical Review', 'Architecture Planning', 'Code Audit', 'Strategy Session'] },
      { id: 'duration', label: 'Expected Duration', type: 'select', options: ['1 hour', '2-3 hours', 'Half day', 'Full day'] }
    ]
  };

  useEffect(() => {
    if (inquiryType && adaptiveQuestionSets[inquiryType]) {
      setAdaptiveQuestions(adaptiveQuestionSets[inquiryType]);
      setCurrentStep(2);
    }
  }, [inquiryType]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare form data for FormSubmit
      const formDataToSubmit = new FormData();
      formDataToSubmit.append('name', formData.name);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('company', formData.company);
      formDataToSubmit.append('phone', formData.phone);
      formDataToSubmit.append('message', formData.message);
      formDataToSubmit.append('inquiryType', inquiryType);
      formDataToSubmit.append('budget', formData.budget);
      formDataToSubmit.append('timeline', formData.timeline);
      formDataToSubmit.append('projectType', formData.projectType);
      formDataToSubmit.append('urgency', formData.urgency);
      formDataToSubmit.append('_captcha', 'false');

      // Submit to FormSubmit
      const response = await fetch('https://formsubmit.co/de700d9f72d4fa24734d02da7a472a6a', {
        method: 'POST',
        body: formDataToSubmit
      });

      if (response.ok) {
        setIsSubmitting(false);
        alert('Thank you for your inquiry! I\'ll get back to you within 24 hours.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: '',
          budget: '',
          timeline: '',
          projectType: '',
          urgency: 'normal'
        });
        setInquiryType('');
        setCurrentStep(1);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setIsSubmitting(false);
      alert('There was an issue sending your inquiry. Please try again or email me directly at ntlemezal35@gmail.com');
    }
  };

  const getInquiryTypeColor = (type) => {
    const inquiry = inquiryTypes.find(i => i.id === type);
    return inquiry ? inquiry.color : 'brand-cyan';
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h4 className="font-orbitron font-bold text-xl text-foreground mb-2">
          What can I help you with?
        </h4>
        <p className="text-text-secondary">
          Select the type of inquiry to get personalized questions
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {inquiryTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => setInquiryType(type.id)}
            className={`p-4 rounded-lg border text-left transition-all duration-300 cyber-hover ${
              inquiryType === type.id
                ? `cyber-border-active bg-${type.color}/10 text-${type.color}`
                : 'border-border hover:border-brand-cyan/50 text-text-secondary hover:text-foreground'
            }`}
          >
            <div className="flex items-start space-x-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                inquiryType === type.id ? `bg-${type.color}/20` : 'bg-surface/50'
              }`}>
                <Icon 
                  name={type.icon} 
                  size={20} 
                  className={inquiryType === type.id ? `text-${type.color}` : 'text-text-secondary'} 
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h5 className="font-medium">{type.name}</h5>
                  <div className={`px-2 py-1 rounded text-xs ${
                    type.priority === 'high' ? 'bg-brand-green/20 text-brand-green' :
                    type.priority === 'medium'? 'bg-warning/20 text-warning' : 'bg-text-tertiary/20 text-text-tertiary'
                  }`}>
                    {type.priority}
                  </div>
                </div>
                <p className="text-sm opacity-80">{type.description}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h4 className="font-orbitron font-bold text-lg text-foreground">
            {inquiryTypes.find(t => t.id === inquiryType)?.name} Details
          </h4>
          <p className="text-text-secondary">Please provide additional information</p>
        </div>
        <button
          onClick={() => {
            setCurrentStep(1);
            setInquiryType('');
          }}
          className="p-2 text-text-secondary hover:text-foreground cyber-hover"
        >
          <Icon name="ArrowLeft" size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            type="text"
            placeholder="Enter your name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
          />
          <Input
            label="Email Address"
            type="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Company/Organization"
            type="text"
            placeholder="Your company name"
            value={formData.company}
            onChange={(e) => handleInputChange('company', e.target.value)}
          />
          <Input
            label="Phone Number"
            type="tel"
            placeholder="+1 (555) 123-4567"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
          />
        </div>

        {/* Adaptive Questions */}
        <div className="space-y-4">
          <h5 className="font-medium text-foreground">Specific Requirements</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {adaptiveQuestions.map((question) => (
              <div key={question.id}>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  {question.label}
                </label>
                <select
                  value={formData[question.id] || ''}
                  onChange={(e) => handleInputChange(question.id, e.target.value)}
                  className="w-full p-3 bg-input border border-border rounded-lg text-foreground focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan transition-colors"
                >
                  <option value="">Select {question.label.toLowerCase()}</option>
                  {question.options.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Project Details / Message
          </label>
          <textarea
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            placeholder="Please describe your project or inquiry in detail..."
            rows={6}
            className="w-full p-3 bg-input border border-border rounded-lg text-foreground focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan transition-colors resize-none"
            required
          />
        </div>

        {/* Submit Button */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1 cyber-border"
            onClick={() => setCurrentStep(1)}
          >
            Back to Selection
          </Button>
          <Button
            type="submit"
            variant="default"
            className={`flex-1 bg-${getInquiryTypeColor(inquiryType)} text-black hover:bg-${getInquiryTypeColor(inquiryType)}/90 cyber-glow`}
            loading={isSubmitting}
            iconName="Send"
            iconPosition="left"
          >
            {isSubmitting ? 'Sending...' : 'Send Inquiry'}
          </Button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="glass-morphism rounded-xl p-6 cyber-border">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-brand-cyan to-brand-green rounded-lg flex items-center justify-center cyber-glow">
          <Icon name="Brain" size={20} color="#ffffff" />
        </div>
        <div>
          <h3 className="font-orbitron font-bold text-lg text-foreground">Intelligent Contact Form</h3>
          <p className="text-sm text-text-secondary">AI-powered inquiry system with adaptive questions</p>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="flex items-center justify-center mb-8">
        <div className="flex items-center space-x-4">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
            currentStep >= 1 ? 'border-brand-cyan bg-brand-cyan text-black' : 'border-border text-text-secondary'
          }`}>
            <span className="text-sm font-medium">1</span>
          </div>
          <div className={`w-16 h-0.5 transition-all duration-300 ${
            currentStep >= 2 ? 'bg-brand-cyan' : 'bg-border'
          }`}></div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
            currentStep >= 2 ? 'border-brand-cyan bg-brand-cyan text-black' : 'border-border text-text-secondary'
          }`}>
            <span className="text-sm font-medium">2</span>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="min-h-[500px]">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
      </div>

      {/* Response Time Commitment */}
      <div className="mt-8 pt-6 border-t border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div className="p-3 bg-surface/30 rounded-lg">
            <Icon name="Clock" size={20} className="text-brand-cyan mx-auto mb-2" />
            <div className="text-sm font-medium text-foreground">Response Time</div>
            <div className="text-xs text-text-secondary">Within 24 hours</div>
          </div>
          <div className="p-3 bg-surface/30 rounded-lg">
            <Icon name="Shield" size={20} className="text-brand-green mx-auto mb-2" />
            <div className="text-sm font-medium text-foreground">Privacy</div>
            <div className="text-xs text-text-secondary">100% Confidential</div>
          </div>
          <div className="p-3 bg-surface/30 rounded-lg">
            <Icon name="Zap" size={20} className="text-brand-purple mx-auto mb-2" />
            <div className="text-sm font-medium text-foreground">Priority Support</div>
            <div className="text-xs text-text-secondary">For existing clients</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntelligentContactForm;